import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { brochuresAPI } from '../services/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PDFViewerModal from '../components/PDFViewerModal';
import bannerImage from '../assets/Banner Image 1.jpeg';
import hboImage from '../assets/bg-hbo-bedrijfskunde.jpg';
import masterImage from '../assets/bg-master-cluster.jpg';
import masterclassImage from '../assets/masterclasses-subs.jpg';
import logo from '../assets/logo.png';
import './Brochures.css';

const ALLOWED_CATEGORIES = ['all', 'hbo', 'master', 'training'];
const SEGMENT_NAV = [
  { key: 'hbo', to: '/brochures/hbo', label: 'HBO Opleidingen' },
  { key: 'master', to: '/brochures/master', label: 'Masteropleidingen' },
  { key: 'training', to: '/brochures/masterclasses', label: 'Masterclass' },
  { key: 'finance', to: '/studiefinanciering', label: 'Studiefinanciering' }
];

const CATEGORY_BANNERS = {
  all: {
    image: bannerImage,
    alt: 'NLBA opleidingen overzicht',
    title: 'Opleidingen',
    subtitle: 'Bekijk per segment onze HBO opleidingen, masteropleidingen en masterclasses.'
  },
  hbo: {
    image: hboImage,
    alt: 'HBO Opleidingen NLBA',
    title: 'HBO Opleidingen',
    subtitle: 'Praktijkgericht bacheloronderwijs voor professionals die willen doorgroeien.'
  },
  master: {
    image: masterImage,
    alt: 'Masteropleidingen NLBA',
    title: 'Masteropleidingen',
    subtitle: 'Verdiepende mastertrajecten met focus op strategie, leiderschap en impact.'
  },
  training: {
    image: masterclassImage,
    alt: 'Masterclasses NLBA',
    title: 'Masterclass',
    subtitle: 'Korte en intensieve programma\'s rondom actuele business thema\'s.'
  }
};

const Brochures = ({ fixedCategory = '' }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [brochures, setBrochures] = useState([]);
  const [loading, setLoading] = useState(true);
  const resolvedFixedCategory = ALLOWED_CATEGORIES.includes(fixedCategory) ? fixedCategory : '';
  const initialCategory = resolvedFixedCategory || searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState(
    ALLOWED_CATEGORIES.includes(initialCategory) ? initialCategory : 'all'
  );
  const [pdfModal, setPdfModal] = useState({ isOpen: false, url: '', title: '' });

  useEffect(() => {
    fetchBrochures();
  }, []);

  useEffect(() => {
    if (resolvedFixedCategory) {
      if (selectedCategory !== resolvedFixedCategory) {
        setSelectedCategory(resolvedFixedCategory);
      }
      return;
    }

    const queryCategory = searchParams.get('category') || 'all';
    if (ALLOWED_CATEGORIES.includes(queryCategory) && queryCategory !== selectedCategory) {
      setSelectedCategory(queryCategory);
    }
    if (!ALLOWED_CATEGORIES.includes(queryCategory) && selectedCategory !== 'all') {
      setSelectedCategory('all');
    }
  }, [searchParams, selectedCategory, resolvedFixedCategory]);

  const fetchBrochures = async () => {
    setLoading(true);
    try {
      const data = await brochuresAPI.getAll();
      const sorted = [...(data || [])].sort((a, b) => {
        const aOrder = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
        const bOrder = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a?.title || '').localeCompare(String(b?.title || ''));
      });
      setBrochures(sorted);
    } catch (err) {
      console.error('Failed to load brochures', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      hbo: t('public.brochures.categories.hbo'),
      master: t('public.brochures.categories.master'),
      training: t('public.brochures.categories.training'),
      other: t('public.brochures.categories.other')
    };
    return labels[category] || category;
  };

  const isPdfBrochure = (brochure) => {
    const mimeType = String(brochure?.file?.mimeType || '').toLowerCase();
    const fileName = String(brochure?.file?.originalName || brochure?.file?.filename || '').toLowerCase();
    return mimeType === 'application/pdf' || fileName.endsWith('.pdf');
  };

  const handleDownload = (brochure) => {
    const downloadUrl = brochuresAPI.downloadUrl(brochure.id);
    window.open(downloadUrl, '_blank');
  };

  const handleView = (brochure) => {
    const viewUrl = brochuresAPI.viewUrl(brochure.id);

    if (!isPdfBrochure(brochure)) {
      window.open(viewUrl, '_blank');
      return;
    }

    setPdfModal({ isOpen: true, url: viewUrl, title: brochure.title });
  };

  const closePdfModal = () => {
    setPdfModal({ isOpen: false, url: '', title: '' });
  };

  const handleCategorySelect = (category) => {
    if (resolvedFixedCategory) return;
    setSelectedCategory(category);
    if (category === 'all') {
      setSearchParams({});
      return;
    }
    setSearchParams({ category });
  };

  const displayBrochures = brochures;
  const finalFiltered = selectedCategory === 'all'
    ? displayBrochures
    : displayBrochures.filter((b) => b.category === selectedCategory);

  const sectionOrder = ['hbo', 'master', 'training'];
  const activeCategoryKey = resolvedFixedCategory || selectedCategory || 'all';
  const activeBanner = CATEGORY_BANNERS[activeCategoryKey] || CATEGORY_BANNERS.all;

  const renderProgramActions = (brochure) => (
    <div style={styles.programActions}>
      <button
        style={styles.programActionPrimary}
        onClick={() => brochure && handleView(brochure)}
        disabled={!brochure}
      >
        Bekijk Brochure
      </button>
      <button
        style={styles.programActionSecondary}
        onClick={() => brochure && handleDownload(brochure)}
        disabled={!brochure}
      >
        Download
      </button>
      <Link to="/inschrijven" style={styles.programActionEnroll}>
        Schrijf je nu in
      </Link>
    </div>
  );

  const getBrochureDetailsText = (brochure) => {
    return String(brochure?.details || '').trim();
  };

  const getBrochureDescriptionText = (brochure) => {
    return String(brochure?.description || '').trim();
  };

  const isHboCategoryActive = selectedCategory === 'hbo';
  const isMasterclassActive = selectedCategory === 'training';

  const quickInfoCards = isHboCategoryActive
    ? [
        {
          title: 'Uw Investering',
          lines: [
            'Inschrijfgeld: SRD 1000,-',
            'Per Collegejaar: SRD 20.000,- (Incl. Digitaal Literatuur)'
          ]
        },
        {
          title: 'Proefcollege',
          lines: ['Meld je vrijblijvend aan via info.suriname@nlba.com']
        },
        {
          title: 'College Tijden',
          lines: ['Fysiek: Maandag - Vrijdag 17:00-21:00']
        }
      ]
    : isMasterclassActive
      ? [
          {
            title: 'Uw Investering',
            lines: ['Opleidingskosten: EUR 700,00']
          },
          {
            title: 'Proefcollege',
            lines: ['Meld je vrijblijvend aan via info.suriname@nlba.com']
          },
          {
            title: 'Praktische tijden',
            lines: ['Online: 14.00 - 17.00', 'Fysiek: 09.00 - 16.00']
          }
        ]
      : [
          {
            title: 'Uw Investering',
            lines: [
              'Inschrijfgeld: EUR 100',
              'Leerjaar 1: EUR 5000',
              'Leerjaar 2: EUR 5000',
              'Thesisfase: EUR 2500'
            ]
          },
          {
            title: 'Proefcollege',
            lines: ['Meld je vrijblijvend aan via info.suriname@nlba.com']
          },
          {
            title: 'Praktische tijden',
            lines: ['Online: 14.00 - 17.00', 'Fysiek: 09.00 - 16.00']
          }
        ];

  const renderBrochureCard = (brochure) => (
    <article key={brochure.id} className="brochure-feature-card">
      <div className="brochure-feature-media">
        {brochure.hasCardImage ? (
          <img
            src={brochuresAPI.cardImageUrl(brochure.id)}
            alt={brochure.title}
            className="brochure-feature-image"
            loading="lazy"
          />
        ) : (
          <div className="brochure-feature-placeholder">Geen afbeelding beschikbaar</div>
        )}
      </div>
      <div className="brochure-feature-content">
        <p className="brochure-feature-category">{getCategoryLabel(brochure.category)}</p>
        <h3 className="brochure-feature-title">{brochure.title}</h3>
        {getBrochureDescriptionText(brochure) && (
          <p className="brochure-feature-summary">{getBrochureDescriptionText(brochure)}</p>
        )}
        {getBrochureDetailsText(brochure) && (
          <p className="brochure-feature-text">{getBrochureDetailsText(brochure)}</p>
        )}
        <div style={styles.programActions}>
          <button
            style={styles.programActionPrimary}
            onClick={() => handleView(brochure)}
          >
            {t('public.brochures.actions.view')}
          </button>
          <button
            style={styles.programActionSecondary}
            onClick={() => handleDownload(brochure)}
          >
            {t('public.brochures.actions.download')}
          </button>
          <Link to="/inschrijven" style={styles.programActionEnroll}>
            Schrijf je nu in
          </Link>
        </div>
      </div>
    </article>
  );

  return (
    <div style={styles.page}>
      <Navbar />
      
      <section
        style={{
          ...styles.hero,
          backgroundImage: `url(${activeBanner.image})`,
        }}
      >
        <div style={styles.heroOverlay} />
        <div style={styles.heroInner}>
          <img src={logo} alt="NLBA logo" style={styles.heroLogo} />
          <h1 style={styles.title}>{resolvedFixedCategory ? activeBanner.title : t('public.brochures.title')}</h1>
          <p style={styles.subtitle}>
            {resolvedFixedCategory ? activeBanner.subtitle : t('public.brochures.subtitle')}
          </p>
          <div style={styles.contactLine}>
            {t('public.brochures.contactLine')}
          </div>
        </div>
      </section>

      <section style={styles.content}>
        <div style={styles.container}>
          {resolvedFixedCategory ? (
            <div className="brochures-segment-layout">
              <aside className="brochures-side-nav-wrap">
                <p className="brochures-side-nav-label">Opleidingen</p>
                <ul className="brochures-side-nav">
                  {SEGMENT_NAV.map((item) => (
                    <li key={item.key}>
                      <Link
                        to={item.to}
                        className={`brochures-side-nav-link${selectedCategory === item.key ? ' active' : ''}`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </aside>

              <div>
                <div style={styles.infoBar}>
                  <p style={styles.infoText}>
                    {t('public.brochures.tip')}
                  </p>
                </div>

                <div style={styles.quickInfoGrid}>
                  {quickInfoCards.map((card) => (
                    <div style={styles.quickInfoCard} key={card.title}>
                      <h3 style={styles.quickInfoTitle}>{card.title}</h3>
                      {card.lines.map((line) => (
                        <p style={styles.quickInfoText} key={line}>{line}</p>
                      ))}
                    </div>
                  ))}
                </div>

                {loading ? (
                  <div style={styles.loading}>{t('common.loading')}</div>
                ) : (
                  <section style={styles.categorySection}>
                    <h2 style={styles.categoryHeading}>{getCategoryLabel(selectedCategory)}</h2>
                    <div className="brochure-stack">
                      {finalFiltered.map(renderBrochureCard)}
                    </div>
                  </section>
                )}

                {!loading && finalFiltered.length === 0 && (
                  <div style={styles.noData}>{t('common.noData')}</div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div style={styles.filterRow}>
                <button
                  style={{ ...styles.filterBtn, ...(selectedCategory === 'all' ? styles.filterBtnActive : {}) }}
                  onClick={() => handleCategorySelect('all')}
                >
                  {t('public.brochures.filters.all')}
                </button>
                <button
                  style={{ ...styles.filterBtn, ...(selectedCategory === 'hbo' ? styles.filterBtnActive : {}) }}
                  onClick={() => handleCategorySelect('hbo')}
                >
                  {t('public.brochures.filters.hbo')}
                </button>
                <button
                  style={{ ...styles.filterBtn, ...(selectedCategory === 'master' ? styles.filterBtnActive : {}) }}
                  onClick={() => handleCategorySelect('master')}
                >
                  {t('public.brochures.filters.master')}
                </button>
                <button
                  style={{ ...styles.filterBtn, ...(selectedCategory === 'training' ? styles.filterBtnActive : {}) }}
                  onClick={() => handleCategorySelect('training')}
                >
                  {t('public.brochures.filters.masterclasses')}
                </button>
              </div>

              <div style={styles.infoBar}>
                <p style={styles.infoText}>
                  {t('public.brochures.tip')}
                </p>
              </div>

              <div style={styles.quickInfoGrid}>
                {quickInfoCards.map((card) => (
                  <div style={styles.quickInfoCard} key={card.title}>
                    <h3 style={styles.quickInfoTitle}>{card.title}</h3>
                    {card.lines.map((line) => (
                      <p style={styles.quickInfoText} key={line}>{line}</p>
                    ))}
                  </div>
                ))}
              </div>

              {loading ? (
                <div style={styles.loading}>{t('common.loading')}</div>
              ) : (
                <>
                  {selectedCategory === 'all' ? (
                    <div style={styles.sectionList}>
                      {sectionOrder.map((category) => {
                        const sectionBrochures = displayBrochures.filter((b) => b.category === category);
                        return (
                          <section key={category} style={styles.categorySection}>
                            <h2 style={styles.categoryHeading}>{getCategoryLabel(category)}</h2>
                            {sectionBrochures.length === 0 ? (
                              <div style={styles.noDataSmall}>{t('common.noData')}</div>
                            ) : (
                              <div className="brochure-stack">
                                {sectionBrochures.map(renderBrochureCard)}
                              </div>
                            )}
                          </section>
                        );
                      })}
                    </div>
                  ) : (
                    <section style={styles.categorySection}>
                      <h2 style={styles.categoryHeading}>{getCategoryLabel(selectedCategory)}</h2>
                      <div className="brochure-stack">
                        {finalFiltered.map(renderBrochureCard)}
                      </div>
                    </section>
                  )}
                </>
              )}

              {!loading && finalFiltered.length === 0 && (
                <div style={styles.noData}>{t('common.noData')}</div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />

      <PDFViewerModal
        isOpen={pdfModal.isOpen}
        onClose={closePdfModal}
        pdfUrl={pdfModal.url}
        title={pdfModal.title}
      />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  },
  hero: {
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: '440px',
    display: 'flex',
    alignItems: 'center',
    color: '#ffffff',
    padding: '5rem 2rem',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(118,0,188,0.75) 0%, rgba(14,77,100,0.70) 55%, rgba(23,126,137,0.45) 100%)',
    zIndex: 0,
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '760px',
    margin: '0 auto',
    textAlign: 'center',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: '#ffffff'
  },
  heroLogo: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    objectFit: 'contain',
    backgroundColor: '#ffffff',
    padding: '6px',
    marginBottom: '16px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  subtitle: {
    fontSize: '1.2rem',
    opacity: 0.95,
    lineHeight: '1.6',
    color: '#ffffff'
  },
  contactLine: {
    marginTop: '1rem',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.9)'
  },
  content: {
    padding: '3rem 2rem',
    flex: 1
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    justifyContent: 'center',
    marginBottom: '2rem'
  },
  filterBtn: {
    padding: '0.75rem 1.5rem',
    border: '2px solid #7600bc',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#7600bc',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  filterBtnLink: {
    padding: '0.75rem 1.5rem',
    border: '2px solid #7600bc',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#7600bc',
    fontSize: '1rem',
    fontWeight: '600',
    textDecoration: 'none',
    transition: 'all 0.2s'
  },
  filterBtnActive: {
    backgroundColor: '#0e4d64',
    color: '#ffffff'
  },
  infoBar: {
    backgroundColor: '#f3f8fa',
    borderLeft: '4px solid #f2a541',
    borderRadius: '8px',
    padding: '0.9rem 1rem',
    marginBottom: '1.5rem'
  },
  infoText: {
    margin: 0,
    color: '#355266'
  },
  quickInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
    marginBottom: '20px'
  },
  quickInfoCard: {
    background: '#fff',
    border: '1px solid #efe6f7',
    borderRadius: '10px',
    padding: '12px'
  },
  quickInfoTitle: {
    margin: '0 0 6px',
    color: '#4f2069'
  },
  quickInfoText: {
    margin: '0 0 4px',
    color: '#374151',
    fontSize: '0.95rem'
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666'
  },
  noData: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666'
  },
  noDataSmall: {
    color: '#6b7280',
    fontSize: '0.95rem',
    padding: '0.5rem 0 1rem'
  },
  sectionList: {
    display: 'grid',
    gap: '1.5rem'
  },
  categorySection: {
    backgroundColor: '#ffffff',
    border: '1px solid #e9e4ef',
    borderRadius: '12px',
    padding: '1rem'
  },
  categoryHeading: {
    margin: '0 0 0.8rem',
    color: '#4f2069',
    fontSize: '1.3rem'
  },
  brochureGrid: {
    display: 'grid',
    gap: '1rem'
  },
  programActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px'
  },
  programActionPrimary: {
    padding: '0.55rem 0.95rem',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#4f2069',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer'
  },
  programActionSecondary: {
    padding: '0.55rem 0.95rem',
    border: '1px solid #4f2069',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#4f2069',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer'
  },
  programActionEnroll: {
    padding: '0.55rem 0.95rem',
    borderRadius: '8px',
    backgroundColor: '#0e4d64',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '600',
    textDecoration: 'none'
  },
  viewBtn: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#f3f3f3',
    color: '#333',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer'
  },
  downloadBtn: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#0e4d64',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

export default Brochures;

